import {
	FC,
	FormEvent,
	ReactElement,
	ReactNode,
	useCallback,
	useEffect,
	useState,
} from 'react';
import {
	Controller,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { getI18n, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/capacitor';
import { FirebaseError } from 'firebase/app';
import { createUserWithEmailAndPassword, getAuth, User } from 'firebase/auth';

import {
	checkBannedEmail,
	TCheckBannedEmailResultAction,
	useBusinessLayer,
	UserActionTypes,
	useSpenditToken,
} from '@mopla/business-logic';
import { FIREBASE_EMAIL_EXISTS_ERR, SuccessStep } from '@mopla/constants';
import {
	Loader,
	MplButton,
	MplErrorModal,
	MplInputV2,
	PasswordInput,
	PasswordValidationConditions,
} from '@mopla/ui';
import { FormTitle } from '@mopla/ui/layouts/WelcomeScreen';
import { getSubmitDisabled, usePersistentCountdownGuard } from '@mopla/utils';

import passApi from '../../../api/passApi';
import {
	pushAuthEvent,
	pushRegisterSuccessEvent,
} from '../../../app/userTracking/events';
import { PrivacyPolicy } from '../PrivacyPolicy/PrivacyPolicy';
import { TermsAndConditions } from '../TermsOfUse/TermsOfUse';

import { SelectFlowStep } from './ui/SelectFlowStep';
import { FormStyled } from './RegistrationForm.styles';
import RegistrationValidationSchema from './RegistrationValidation';
import { ERegistrationFormSteps } from './types';
import { VerifyEmailSentView } from './VerifyEmailSentView';

interface IFormInput {
	newEmail: string;
	newPassword: string;
	emailBanned?: string;
	serverError?: string;
}

interface RegistrationFormProps {
	onSuccess(successType: SuccessStep | null): void;
	showVerifyEmailSent: boolean;
	activeRegStep: ERegistrationFormSteps;
	setActiveRegStep(step: ERegistrationFormSteps): void;
	render: (p: { content: ReactNode; submit: ReactNode }) => ReactElement;
}

export const RegistrationForm: FC<RegistrationFormProps> = ({
	onSuccess,
	showVerifyEmailSent,
	activeRegStep,
	setActiveRegStep,
	render,
}) => {
	const navigate = useNavigate();
	const bl = useBusinessLayer();
	const [loading, setLoading] = useState(false);
	const [regEmail, setRegEmail] = useState('');
	const [termsOfUse, setTermsOfUse] = useState(false);
	const [privacyPolicy, setPrivacyPolicy] = useState(false);
	const { t } = useTranslation([
		'registration',
		'logIn',
		'verify',
		'validation',
	]);
	const auth = getAuth();
	const { connectTokenToUser } = useSpenditToken();
	const { reinitCountdownGuard } = usePersistentCountdownGuard('emailSendTime');

	const methods = useForm<IFormInput>({
		defaultValues: {
			newEmail: '',
			newPassword: '',
		},
		resolver: yupResolver(RegistrationValidationSchema),
		mode: 'onTouched',
	});
	const {
		formState: { errors: formErrors, touchedFields },
		handleSubmit,
		getValues,
		watch,
		trigger,
		resetField,
		clearErrors,
		setError,
	} = methods;

	useEffect(() => {
		clearErrors('newPassword');
	}, [activeRegStep, clearErrors]);

	/**
	 * Creates a firebase user by {signUpFn},
	 * completes registration flow:
	 *   * mopla user is created
	 *   * spenditToken is connected
	 *   * !emailVerified ? proper registration step is set
	 *   * emailVerified ? redirect to personalDetails screen
	 * */
	const processSignUp = async (signUpFn: () => Promise<User>) => {
		setLoading(true);

		try {
			const user = await signUpFn();
			console.log('processRegistrationFlow', { user });

			await passApi.post('/api/passengers/create', {
				authenticationProviderId: user.uid,
				email: user.email,
				language: getI18n().language,
			});

			connectTokenToUser();

			if (user.emailVerified) {
				navigate({ pathname: '/welcome', search: 'personalDetails' });
			} else {
				reinitCountdownGuard();

				setRegEmail(user.email as string);
				setActiveRegStep(ERegistrationFormSteps.Password);

				onSuccess(SuccessStep.verifyEmail);
			}

			pushRegisterSuccessEvent();
			pushAuthEvent('sign_up', 'email');
		} catch (err) {
			setActiveRegStep(ERegistrationFormSteps.Email);
			let errorText = 'email.common';

			if ((err as FirebaseError).code === FIREBASE_EMAIL_EXISTS_ERR) {
				errorText = 'email.exist';
			}

			setError('newEmail', { message: errorText });

			Sentry.captureException(err);
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const processEmailPasswordSignUp: SubmitHandler<IFormInput> = async (
		data
	) => {
		const signUpByUserCredentials = async () => {
			const { newEmail, newPassword } = data;

			const userCredentials = await createUserWithEmailAndPassword(
				auth,
				newEmail,
				newPassword
			);

			const { user } = userCredentials;
			await user.getIdTokenResult();

			return user;
		};

		await processSignUp(signUpByUserCredentials);
	};

	const onSubmitResendEmail = async () => {
		const user = auth.currentUser;

		if (user) {
			setLoading(true);

			try {
				await passApi.post('/api/command/verifyEmail', { app: 'PASSENGER' });
			} catch (e) {
				Sentry.captureException(e);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleCheckBannedEmail = (email: string) => {
		return new Promise<boolean>((resolve, reject) => {
			setLoading(true);
			bl.dispatch(checkBannedEmail({ email }));

			bl.watchActions({
				types: UserActionTypes.CheckBannedEmailResult,
				callback: (_action) => {
					const action = _action as TCheckBannedEmailResultAction;

					setLoading(false);

					if (action.payload?.error) {
						setError('serverError', {});
						return reject();
					}

					resolve(!action.payload.isValid);
				},
			});
		});
	};

	const handleBannedEmailOkClick = useCallback(() => {
		clearErrors('emailBanned');
		resetField('newEmail');
		resetField('newPassword');
		setActiveRegStep(ERegistrationFormSteps.Email);
	}, [clearErrors, resetField, setActiveRegStep]);

	const handleSubmitForm = async () => {
		const email = getValues('newEmail');

		if (activeRegStep === ERegistrationFormSteps.Email) {
			const isEmailValid = await trigger('newEmail');
			const isEmailBanned = await handleCheckBannedEmail(email);

			if (isEmailBanned) {
				setError('emailBanned', {});
				return;
			}

			if (!isEmailValid) {
				return;
			}

			setActiveRegStep(ERegistrationFormSteps.Password);
		}

		if (activeRegStep === ERegistrationFormSteps.Password) {
			return handleSubmit(processEmailPasswordSignUp)();
		}
	};

	const handleSkipServerError = useCallback(() => {
		clearErrors('serverError');
	}, [clearErrors]);

	if (showVerifyEmailSent) {
		return (
			<VerifyEmailSentView
				email={regEmail}
				onResend={onSubmitResendEmail}
				render={render}
			/>
		);
	}

	if (loading) {
		return render({ content: <Loader delay={100} />, submit: null });
	}

	if (activeRegStep === ERegistrationFormSteps.SelectFlow) {
		return (
			<SelectFlowStep render={render} setActiveRegStep={setActiveRegStep} />
		);
	}

	const isRegistrationSubmitDisabled =
		!termsOfUse ||
		!privacyPolicy ||
		getSubmitDisabled(methods, { expectDirtyField: 'newPassword' });

	return render({
		content: (
			<>
				<Stack className="content">
					{activeRegStep === ERegistrationFormSteps.Email && (
						<FormTitle testId="registrationForm-email-title">
							{t('text.title1')}
						</FormTitle>
					)}
					{activeRegStep === ERegistrationFormSteps.Password && (
						<FormTitle testId="registrationForm-password-title">
							{t('text.title2')}
						</FormTitle>
					)}
					<FormProvider {...methods}>
						<FormStyled
							id="registrationForm"
							autoComplete="off"
							onSubmit={(e: FormEvent<HTMLFormElement>) => {
								e.preventDefault();
								handleSubmitForm();
							}}
						>
							{activeRegStep === ERegistrationFormSteps.Email && (
								<Controller
									name="newEmail"
									render={({ field }) => (
										<MplInputV2
											{...field}
											variant="outlined"
											onChange={(e) => {
												e.target.value = e.target.value.trim();
												field.onChange(e);
											}}
											error={Boolean(formErrors.newEmail?.message)}
											label={t('label.email')}
											helperText={
												formErrors.newEmail &&
												t(`validation:${formErrors.newEmail?.message}`)
											}
											inputProps={{
												'data-testid': 'registrationForm-email-textField',
											}}
											clearable
											autoFocus
											type="email"
											autoComplete="username email"
										/>
									)}
								/>
							)}
							{activeRegStep === ERegistrationFormSteps.Password && (
								<>
									<Controller
										name="newPassword"
										render={({ field }) => (
											<PasswordInput
												{...field}
												autoComplete="new-password"
												error={Boolean(
													formErrors.newPassword?.message ===
														'password.no_spaces'
												)}
												label={t('label.password')}
												helperText={
													formErrors.newPassword?.message ===
														'password.no_spaces' &&
													t(`validation:${formErrors.newPassword?.message}`)
												}
												inputProps={{
													'data-testid': 'registrationForm-password-textField',
												}}
											/>
										)}
									/>
									<PasswordValidationConditions
										password={watch('newPassword')}
										fieldTouched={touchedFields.newPassword}
										checkPasswordErrors={Boolean(
											touchedFields.newPassword && formErrors.newPassword
										)}
									/>
									<Stack mt="auto" overflow="hidden" gap={2}>
										<Typography variant="b2" color="solutions.600">
											{t('text.requiredFieldsMarker')}
										</Typography>
										<TermsAndConditions
											checked={termsOfUse}
											toggleCheckbox={() => setTermsOfUse((prev) => !prev)}
										/>
										<PrivacyPolicy
											checked={privacyPolicy}
											toggleCheckbox={() => setPrivacyPolicy((prev) => !prev)}
										/>
									</Stack>
								</>
							)}
						</FormStyled>
					</FormProvider>
				</Stack>
				<MplErrorModal
					open={Boolean(formErrors.emailBanned)}
					title={t('validation:email.banned.title')}
					content={t('validation:email.banned.text')}
					testId="RegistrationFormMplModal-emailBanned"
					buttons={
						<MplButton
							fullWidth
							color="primaryButtonV2"
							onClick={handleBannedEmailOkClick}
						>
							{t('validation:email.banned.button')}
						</MplButton>
					}
				/>
				<MplErrorModal
					open={Boolean(formErrors.serverError)}
					testId="RegistrationFormMplModal-serverError"
					buttons={
						<MplButton
							fullWidth
							color="primaryButtonV2"
							onClick={handleSkipServerError}
						>
							{t('errors:button.close')}
						</MplButton>
					}
				/>
			</>
		),
		submit: (
			<>
				{activeRegStep === ERegistrationFormSteps.Email && (
					<MplButton
						fullWidth
						color="primaryButtonV2"
						data-testid="registration-create-account-submit-button"
						disabled={getSubmitDisabled(methods)}
						type="submit"
						form="registrationForm"
					>
						{t('button.continue')}
					</MplButton>
				)}
				{activeRegStep === ERegistrationFormSteps.Password && (
					<MplButton
						fullWidth
						color="primaryButtonV2"
						data-testid="registration-create-account-submit-button"
						disabled={isRegistrationSubmitDisabled}
						type="submit"
						form="registrationForm"
					>
						{t('button.submit')}
					</MplButton>
				)}
			</>
		),
	});
};
